import { memo, useCallback, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AppModal } from '../ui/AppModal/index.js';
import { Button } from './surfacePrimitives.js';
import { SegmentTabs, type SegmentTabItem } from '../ui/SegmentTabs.js';
import { StickySaveBar } from './StickySaveBar.js';
import {
  adjacentCreateWizardStep,
  resolveCreateWizardStepIndex,
} from './entityUnifiedModalOrchestration.js';
import {
  KIOSK_UNIFIED_TAB_BODY_CLASS,
  ENTITY_MODAL_TAB_CONTENT_SHELL_CLASS,
  ENTITY_MODAL_HEADER_META_CLASS,
} from './entityUnifiedModalStyles.js';
import styles from './EntityUnifiedModal.module.css';

/** Shared chrome for entity unified modals (Detail≡Upravit + Lifecycle tab DoD). */
export const ENTITY_UNIFIED_MODAL_CLASS =
  'entity-unified-modal flex min-h-full w-full flex-1 flex-col gap-4';

/** Alias — tab body is viewport container; section grids use `@container/entity-modal` on reading shell. */
export const ENTITY_UNIFIED_TAB_BODY_CLASS = KIOSK_UNIFIED_TAB_BODY_CLASS;

/**
 * Operator entry modes for entity overlays:
 * - `create` — new entity
 * - `manage` — single inspect+edit surface (no view|edit fork)
 */
export type EntityUnifiedModalMode = 'create' | 'manage';

export interface EntityUnifiedModalTab {
  readonly id: string;
  readonly label: string;
}

/**
 * Optional create-wizard chrome (QrLabelPrintWizard pattern).
 * When set, SegmentTabs are replaced by a non-clickable step rail and the footer
 * becomes Cancel | Back | Next | final create (last step). Manage/edit omit this.
 */
export interface EntityUnifiedCreateStepMode {
  /** Next / final create gated until true. */
  readonly canAdvance: boolean;
  /** Inline StickySaveBar hint when primary is disabled. */
  readonly disabledReason?: string | null;
  /** Last-step primary label (e.g. Přidat prodejní místo). */
  readonly finalPrimaryLabel: string;
  /** `form=` id for final create submit. */
  readonly finalPrimaryFormId: string;
  /** Extra disable for final create (busy / submitDisabled). */
  readonly finalPrimaryDisabled?: boolean;
}

export interface EntityUnifiedModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly tabs: readonly EntityUnifiedModalTab[];
  readonly activeTabId: string;
  readonly onTabChange: (tabId: string) => void;
  readonly children: ReactNode;
  /** Optional badge/meta row under the title (id, location, status). */
  readonly headerMeta?: ReactNode;
  readonly footer?: ReactNode;
  readonly isBusy?: boolean;
  readonly size?: 'small' | 'medium' | 'large' | 'xlarge';
  readonly tabsAriaLabel?: string;
  readonly testId?: string;
  readonly className?: string;
  /**
   * Optional test/observability hook — mirrored as `data-live-operational-mode` on the
   * modal body (SalesPointUnifiedModal G1 RTL asserts DONATION seed without chrome-only).
   */
  readonly dataLiveOperationalMode?: string;
  /**
   * Forwards to AppModal. Use `hidden` when a nested catalog (e.g. category emoji)
   * must be the sole scrollport so sticky chrome inside that catalog works.
   */
  readonly bodyOverflow?: 'auto' | 'hidden';
  /**
   * Forwards to AppModal. Prefer {@link permissionTrapReleased} for geo — flipping
   * `modal` remounts Dialog.Content and can drop in-flight locate.
   */
  readonly modal?: boolean;
  /**
   * Forwards to AppModal — sets `data-geo-permission-trap="released"` without remount.
   */
  readonly permissionTrapReleased?: boolean;
  /**
   * Create-wizard shell: step rail + Cancel|Back|Next|final create.
   * When set, owns footer (ignores `footer`). Manage/edit keep freestyle SegmentTabs.
   */
  readonly createStepMode?: EntityUnifiedCreateStepMode;
}

/**
 * Shared entity modal shell: AppModal + SegmentTabs (incl. Lifecycle) + tab body.
 * FA-06: `isBusy` forwards to AppModal busy-dismiss (Escape / backdrop / close).
 * Surfaces own tab content; kit owns layout/spacing/footer slot.
 * Optional `createStepMode` swaps SegmentTabs for a non-clickable step rail + wizard footer.
 */
export const EntityUnifiedModal = memo(({
  open,
  onClose,
  title,
  tabs,
  activeTabId,
  onTabChange,
  children,
  headerMeta,
  footer,
  isBusy = false,
  size = 'xlarge',
  tabsAriaLabel,
  testId = 'entity-unified-modal',
  className,
  dataLiveOperationalMode,
  bodyOverflow,
  modal,
  permissionTrapReleased,
  createStepMode,
}: EntityUnifiedModalProps): JSX.Element => {
  const { t } = useTranslation('admin');
  const wizardActive = createStepMode != null;
  const stepIds = useMemo(() => tabs.map((tab) => tab.id), [tabs]);
  const stepIndex = resolveCreateWizardStepIndex(stepIds, activeTabId);
  const isFirstStep = stepIndex <= 0;
  const isLastStep = stepIndex >= stepIds.length - 1 || stepIds.length === 0;

  const segmentTabs: readonly SegmentTabItem[] = tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
  }));

  /**
   * Nested catalog scroll (e.g. category emoji): AppModal body is overflow:hidden, so the
   * height chain must use min-h-0 + overflow-hidden or the catalog grows to content height
   * and never becomes a mobile touch scrollport.
   */
  const nestedCatalogScroll = bodyOverflow === 'hidden';

  const rootClass = [
    'entity-unified-modal kiosk-unified-modal flex w-full flex-1 flex-col gap-4',
    nestedCatalogScroll ? 'min-h-0 overflow-hidden' : 'min-h-full',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const tabBodyClass = [
    'entity-modal-tab-body flex flex-1 flex-col @container/entity-modal-viewport',
    nestedCatalogScroll ? 'min-h-0 overflow-hidden' : 'min-h-full',
  ].join(' ');

  const tabContentClass = [
    ENTITY_MODAL_TAB_CONTENT_SHELL_CLASS,
    nestedCatalogScroll ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const handleWizardBack = useCallback((): void => {
    const prev = adjacentCreateWizardStep(stepIds, activeTabId, 'back');
    if (prev != null) {
      onTabChange(prev);
    }
  }, [activeTabId, onTabChange, stepIds]);

  const handleWizardNext = useCallback((): void => {
    if (!createStepMode?.canAdvance) {
      return;
    }
    const next = adjacentCreateWizardStep(stepIds, activeTabId, 'next');
    if (next != null) {
      onTabChange(next);
    }
  }, [activeTabId, createStepMode?.canAdvance, onTabChange, stepIds]);

  const wizardFooter = wizardActive && createStepMode != null ? (
    <StickySaveBar
      embedded
      hint={
        !createStepMode.canAdvance || (isLastStep && createStepMode.finalPrimaryDisabled)
          ? (createStepMode.disabledReason ?? '')
          : ''
      }
      hintId={
        createStepMode.disabledReason != null && createStepMode.disabledReason !== ''
          ? `${testId}-create-step-disabled-reason`
          : undefined
      }
    >
      <Button type="button" intent="secondary" disabled={isBusy} onClick={onClose}>
        {t('shared.entityModal.createWizardCancel', { defaultValue: 'Cancel' })}
      </Button>
      {!isFirstStep ? (
        <Button type="button" intent="secondary" disabled={isBusy} onClick={handleWizardBack}>
          {t('shared.entityModal.createWizardBack', { defaultValue: 'Back' })}
        </Button>
      ) : null}
      {!isLastStep ? (
        <Button
          type="button"
          intent="primary"
          disabled={isBusy || !createStepMode.canAdvance}
          onClick={handleWizardNext}
          aria-describedby={
            !createStepMode.canAdvance && createStepMode.disabledReason
              ? `${testId}-create-step-disabled-reason`
              : undefined
          }
          data-testid={`${testId}-create-next`}
        >
          {t('shared.entityModal.createWizardNext', { defaultValue: 'Next' })}
        </Button>
      ) : (
        <Button
          type="submit"
          form={createStepMode.finalPrimaryFormId}
          intent="primary"
          disabled={
            isBusy || !createStepMode.canAdvance || createStepMode.finalPrimaryDisabled === true
          }
          aria-describedby={
            (!createStepMode.canAdvance || createStepMode.finalPrimaryDisabled === true) &&
            createStepMode.disabledReason
              ? `${testId}-create-step-disabled-reason`
              : undefined
          }
          data-testid={`${testId}-create-submit`}
        >
          {createStepMode.finalPrimaryLabel}
        </Button>
      )}
    </StickySaveBar>
  ) : undefined;

  const resolvedFooter = wizardActive ? wizardFooter : footer;

  return (
    <AppModal
      isOpen={open}
      onClose={onClose}
      title={title}
      size={size}
      isBusy={isBusy}
      footer={resolvedFooter}
      testId={testId}
      bodyOverflow={bodyOverflow}
      modal={modal}
      permissionTrapReleased={permissionTrapReleased}
    >
      <div
        className={rootClass}
        data-testid={`${testId}-body`}
        data-create-step-mode={wizardActive ? 'true' : undefined}
        {...(dataLiveOperationalMode != null
          ? { 'data-live-operational-mode': dataLiveOperationalMode }
          : {})}
      >
        {headerMeta ? (
          <div className={ENTITY_MODAL_HEADER_META_CLASS}>
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--color-an-border)] pb-4">
              {headerMeta}
            </div>
          </div>
        ) : null}

        {wizardActive && tabs.length > 0 ? (
          <div
            className={styles.stepProgress}
            aria-label={t('shared.entityModal.createWizardStepsCaption', {
              defaultValue: 'Wizard steps',
            })}
            data-testid={`${testId}-create-step-progress`}
          >
            <p className={styles.stepProgressCaption}>
              {t('shared.entityModal.createWizardStepsHint', {
                defaultValue:
                  'Steps {{current}} of {{total}} — use Back / Next below (this list is not clickable).',
                current: stepIndex + 1,
                total: tabs.length,
              })}
            </p>
            <ol className={styles.stepNav}>
              {tabs.map((tab, index) => {
                const active = tab.id === activeTabId;
                const complete = stepIndex > index;
                return (
                  <li
                    key={tab.id}
                    className={styles.stepNavItem}
                    data-active={active ? 'true' : 'false'}
                    data-complete={complete ? 'true' : 'false'}
                    aria-current={active ? 'step' : undefined}
                  >
                    <span className={styles.stepNavIndex} aria-hidden>
                      {index + 1}
                    </span>
                    <span className={styles.stepNavLabel}>{tab.label}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        ) : null}

        {!wizardActive && tabs.length > 1 ? (
          /* Neutralize SegmentTabs list `mb-4` — parent `gap-4` owns vertical rhythm. */
          <div className="shrink-0 [&_[role=tablist]]:mb-0">
            <SegmentTabs
              tabs={segmentTabs}
              activeId={activeTabId}
              onChange={onTabChange}
              ariaLabel={tabsAriaLabel}
              variant="analytics"
            />
          </div>
        ) : null}

        <div className={tabBodyClass} data-testid={`${testId}-tab-body`}>
          <div className={tabContentClass}>{children}</div>
        </div>
      </div>
    </AppModal>
  );
});

EntityUnifiedModal.displayName = 'EntityUnifiedModal';
